import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  InputNumber,
  Modal,
  Radio,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { currency } from '../../utils/utils';
import TouInlineEditor from './TouInlineEditor';
import {
  factoryQueryId,
  useCopyTouPricing,
  useFetchTouMonthStatus,
  useFetchTouPricingDetail,
  useFetchTouReadiness,
  useUpsertTouPricing,
} from './Hooks';
import { daysInMonthFor } from './touFormUtils';

const { Text, Title } = Typography;

const factoryLabel = (row, fallback) => {
  if (!row) return fallback;
  if (row.kind === 'CHILD' && row.name) {
    return row.number ? `${row.name} (${row.number})` : row.name;
  }
  return row.number || row.name || fallback;
};

const statusColor = {
  COMPLETE: 'green',
  HAS_GAPS: 'orange',
  NOT_SET: 'default',
};

const rowId = (row) => String(row?.factoryId ?? '');

const sortFactoryRows = (a, b) => {
  if (a.isFallback) return -1;
  if (b.isFallback) return 1;
  if (Boolean(a.hadCharging) !== Boolean(b.hadCharging)) return a.hadCharging ? -1 : 1;
  return (
    String(a.number || '').localeCompare(String(b.number || '')) ||
    String(a.name || '').localeCompare(String(b.name || ''))
  );
};

/** Nest CHILD factories under their PARENT for the left list. */
function buildTouFactoryTree(rows = []) {
  const nodes = new Map(rows.map((row) => [rowId(row), { ...row, children: [] }]));
  const nestedChildIds = new Set();
  const syntheticParents = new Map();

  rows.forEach((row) => {
    if (row.kind !== 'CHILD' || !row.parentFactoryId) return;
    const parentId = String(row.parentFactoryId);
    const childNode = nodes.get(rowId(row));
    if (!childNode) return;

    if (nodes.has(parentId)) {
      nodes.get(parentId).children.push(childNode);
      nestedChildIds.add(rowId(row));
      return;
    }

    if (!syntheticParents.has(parentId)) {
      syntheticParents.set(parentId, {
        factoryId: `group:${parentId}`,
        number: '',
        name: row.parentName || '',
        kind: 'PARENT',
        parentFactoryId: null,
        isGroupHeader: true,
        status: null,
        children: [],
      });
    }
    syntheticParents.get(parentId).children.push(childNode);
    nestedChildIds.add(rowId(row));
  });

  const roots = [
    ...[...syntheticParents.values()].filter((node) => node.children.length),
    ...rows.filter((row) => !nestedChildIds.has(rowId(row))).map((row) => nodes.get(rowId(row))),
  ].filter(Boolean);

  roots.forEach((node) => {
    if (node.children?.length) node.children.sort(sortFactoryRows);
    else delete node.children;
  });

  return roots.sort(sortFactoryRows);
}

function factorySearchText(row) {
  return `${row.number || ''} ${row.name || ''} ${row.parentName || ''}`.toLowerCase();
}

function filterTouFactoryTree(nodes, query) {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;
  return nodes
    .map((node) => {
      const selfMatch = factorySearchText(node).includes(q);
      const childMatches = (node.children || []).filter((child) =>
        factorySearchText(child).includes(q),
      );
      if (selfMatch) return node;
      if (childMatches.length) return { ...node, children: childMatches };
      return null;
    })
    .filter(Boolean);
}

function detectPreset(tariff, year, month) {
  const periods = tariff?.periods || [];
  const lastDay = daysInMonthFor(year, month);
  if (periods.length !== 1) return 'custom';
  const period = periods[0];
  if (Number(period.dateFrom) !== 1 || Number(period.dateTo) !== lastDay) return 'custom';
  const slots = period.slots || [];
  if (
    slots.length === 1 &&
    slots[0].startTime === '00:00' &&
    (slots[0].endTime === '24:00' || slots[0].endTime === '23:59')
  ) {
    return 'flat';
  }
  const wanted = [
    ['06:00', '17:00'],
    ['17:00', '22:00'],
    ['22:00', '24:00'],
    ['00:00', '06:00'],
  ];
  if (slots.length === 3 || slots.length === 4) {
    const keys = slots.map((slot) => `${slot.startTime}-${slot.endTime}`);
    const night = slots.find((slot) => slot.startTime === '22:00');
    const morning = slots.find((slot) => slot.startTime === '00:00' && slot.endTime === '06:00');
    const day = slots.find((slot) => slot.startTime === '06:00' && slot.endTime === '17:00');
    const evening = slots.find((slot) => slot.startTime === '17:00' && slot.endTime === '22:00');
    if (day && evening && (night || morning)) return 'bands';
    if (wanted.every(([start, end]) => keys.includes(`${start}-${end}`))) return 'bands';
  }
  return 'custom';
}

function bandRate(tariff, start) {
  const slots = tariff?.periods?.[0]?.slots || [];
  return slots.find((slot) => slot.startTime === start)?.rate;
}

function RateBar({ mode, day, evening, night, flat }) {
  const segments =
    mode === 'flat'
      ? [{ flex: 24, color: '#1677ff', label: flat != null ? `${flat}` : '' }]
      : [
          { flex: 6, color: '#1d39c4', label: night },
          { flex: 11, color: '#faad14', label: day },
          { flex: 5, color: '#fa8c16', label: evening },
          { flex: 2, color: '#1d39c4', label: night },
        ];
  return (
    <div className="mt-3 overflow-hidden rounded-md" style={{ display: 'flex', height: 28 }}>
      {segments.map((segment, index) => (
        <div
          key={`${segment.color}-${index}`}
          style={{
            flex: segment.flex,
            background: segment.color,
            color: '#fff',
            fontSize: 11,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {segment.label != null && segment.label !== '' ? segment.label : ''}
        </div>
      ))}
    </div>
  );
}

export function FactoryTouEditor({
  operatorId,
  year,
  month,
  factoryId,
  onDirtyChange,
  extraActions = null,
}) {
  const { t } = useTranslation('touPricing');
  const detail = useFetchTouPricingDetail(operatorId, year, month, true, factoryId);
  const upsert = useUpsertTouPricing();
  const copy = useCopyTouPricing();
  const tariff = detail.data?.tariff;
  const [mode, setMode] = useState('flat');
  const [flat, setFlat] = useState(null);
  const [day, setDay] = useState(null);
  const [evening, setEvening] = useState(null);
  const [night, setNight] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [customDirty, setCustomDirty] = useState(false);
  const [customToolbar, setCustomToolbar] = useState(null);

  useEffect(() => {
    const next = detectPreset(tariff, year, month);
    setMode(next === 'custom' && !tariff ? 'flat' : next);
    setFlat(tariff?.periods?.[0]?.slots?.[0]?.rate ?? null);
    setDay(bandRate(tariff, '06:00') ?? null);
    setEvening(bandRate(tariff, '17:00') ?? null);
    const nightSlot = bandRate(tariff, '22:00') ?? bandRate(tariff, '00:00');
    setNight(nightSlot ?? null);
    setDirty(false);
    setCustomDirty(false);
  }, [tariff, year, month, factoryId]);

  useEffect(() => {
    onDirtyChange?.(mode === 'custom' ? customDirty : dirty || upsert.isPending);
  }, [dirty, customDirty, mode, upsert.isPending, onDirtyChange]);

  const lastDay = daysInMonthFor(year, month);
  const savePreset = async () => {
    const slots =
      mode === 'flat'
        ? [{ startTime: '00:00', endTime: '24:00', rate: Number(flat) }]
        : [
            { startTime: '00:00', endTime: '06:00', rate: Number(night) },
            { startTime: '06:00', endTime: '17:00', rate: Number(day) },
            { startTime: '17:00', endTime: '22:00', rate: Number(evening) },
            { startTime: '22:00', endTime: '24:00', rate: Number(night) },
          ];
    if (slots.some((slot) => !Number.isFinite(slot.rate) || slot.rate < 0)) {
      message.error(t('coverageIncomplete'));
      return;
    }
    await upsert.mutateAsync({
      operatorId,
      year,
      month,
      factoryId: factoryQueryId(factoryId),
      periods: [{ dateFrom: 1, dateTo: lastDay, slots }],
      status: 'ACTIVE',
    });
    setDirty(false);
    message.success(t('saveSuccess'));
  };

  const copyPrevious = async () => {
    const prev = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
    await copy.mutateAsync({
      operatorId,
      sourceYear: prev.year,
      sourceMonth: prev.month,
      factoryId: factoryQueryId(factoryId),
      sourceFactoryId: factoryQueryId(factoryId),
      targets: [{ year, month }],
    });
    message.success(t('copySuccess'));
  };

  if (mode === 'custom') {
    return (
      <div>
        <Space className="mb-3" wrap>
          <Radio.Group
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              if (event.target.value === 'custom') setCustomDirty(true);
              else setDirty(true);
            }}
          >
            <Radio.Button value="flat">{t('presetFlat')}</Radio.Button>
            <Radio.Button value="bands">{t('presetBands')}</Radio.Button>
            <Radio.Button value="custom">{t('presetCustom')}</Radio.Button>
          </Radio.Group>
          <Button onClick={copyPrevious} loading={copy.isPending}>
            {t('copyFromPrevious')}
          </Button>
          {customToolbar ? (
            <span className="inline-flex items-center gap-2">{customToolbar}</span>
          ) : null}
        </Space>
        <TouInlineEditor
          key={`${operatorId}-${year}-${month}-${factoryId}`}
          operatorId={operatorId}
          year={year}
          month={month}
          factoryId={factoryQueryId(factoryId)}
          onCoverageChange={(next) => setCustomDirty(Boolean(next?.dirty || next?.isSaving))}
          onToolbarChange={setCustomToolbar}
          extraActions={extraActions}
        />
      </div>
    );
  }

  return (
    <div>
      <Space className="mb-3" wrap>
        <Radio.Group
          value={mode}
          onChange={(event) => {
            setMode(event.target.value);
            if (event.target.value === 'custom') setCustomDirty(true);
            else setDirty(true);
          }}
        >
          <Radio.Button value="flat">{t('presetFlat')}</Radio.Button>
          <Radio.Button value="bands">{t('presetBands')}</Radio.Button>
          <Radio.Button value="custom">{t('presetCustom')}</Radio.Button>
        </Radio.Group>
        <Button onClick={copyPrevious} loading={copy.isPending}>
          {t('copyFromPrevious')}
        </Button>
        <Button type="primary" onClick={savePreset} loading={upsert.isPending}>
          {t('save')}
        </Button>
        {extraActions}
      </Space>
      {mode === 'flat' ? (
        <InputNumber
          min={0}
          precision={6}
          step={0.000001}
          decimalSeparator=","
          value={flat}
          addonBefore="00:00–24:00"
          addonAfter={`${currency}/kWh`}
          onChange={(value) => {
            setFlat(value);
            setDirty(true);
          }}
        />
      ) : (
        <Space wrap size="middle">
          <Space size="small">
            <Text type="secondary">{t('bandDay')}</Text>
            <InputNumber min={0} precision={6} step={0.000001} decimalSeparator="," value={day} onChange={(value) => { setDay(value); setDirty(true); }} />
          </Space>
          <Space size="small">
            <Text type="secondary">{t('bandEvening')}</Text>
            <InputNumber min={0} precision={6} step={0.000001} decimalSeparator="," value={evening} onChange={(value) => { setEvening(value); setDirty(true); }} />
          </Space>
          <Space size="small">
            <Text type="secondary">{t('bandNight')}</Text>
            <InputNumber min={0} precision={6} step={0.000001} decimalSeparator="," value={night} onChange={(value) => { setNight(value); setDirty(true); }} />
          </Space>
        </Space>
      )}
      <RateBar mode={mode} day={day} evening={evening} night={night} flat={flat} />
      <Text type="secondary" className="mt-2 block text-xs">
        {mode === 'flat' ? t('presetFlatHelp') : t('presetBandsHelp')}
      </Text>
      {detail.isLoading ? <Text type="secondary">{t('loading')}</Text> : null}
    </div>
  );
}

export default function FactoryTouBoard({
  operatorId,
  year,
  month,
  reportMode = false,
  onlyFactoryId = null,
  onlyFactoryNumber = '',
  onReadyChange,
}) {
  const { t } = useTranslation('touPricing');
  const [params] = useSearchParams();
  const statusQuery = useFetchTouMonthStatus(operatorId, year, month, !!operatorId);
  const readinessQuery = useFetchTouReadiness(operatorId, year, month, reportMode && !!operatorId);
  const copy = useCopyTouPricing();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(params.get('factoryId') || '');
  const [dirty, setDirty] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState([]);

  const rows = useMemo(() => {
    const data = statusQuery.data;
    if (!data) return [];
    const used = new Set((readinessQuery.data?.usedFactoryIds || []).map(String));
    const extras = (readinessQuery.data?.factories || []).filter(
      (row) => !data.factories.some((item) => String(item.factoryId) === String(row.factoryId)),
    );
    const list = [
      ...data.factories
        .filter((row) => factoryQueryId(row.factoryId) !== 'fallback')
        .filter((row) => row.kind !== 'PARENT' || used.has(String(row.factoryId)))
        .map((row) => ({
          ...row,
          hadCharging: used.has(String(row.factoryId)),
        })),
      ...extras
        .filter((row) => factoryQueryId(row.factoryId) !== 'fallback')
        .map((row) => ({ ...row, hadCharging: true })),
    ];
    const wantedId = onlyFactoryId ? factoryQueryId(onlyFactoryId) : null;
    const wantedNumber = String(onlyFactoryNumber || '').trim().toLowerCase();
    let filtered = list;
    if (wantedId && wantedId !== 'fallback') {
      filtered = list.filter((row) => factoryQueryId(row.factoryId) === wantedId);
    } else if (wantedNumber) {
      const matched = list.filter(
        (row) => String(row.number || '').trim().toLowerCase() === wantedNumber,
      );
      filtered = matched.length
        ? matched
        : list.filter((row) => row.isFallback || factoryQueryId(row.factoryId) === 'fallback');
    } else if (wantedId === 'fallback') {
      filtered = list.filter((row) => row.isFallback || factoryQueryId(row.factoryId) === 'fallback');
    }
    return filtered.sort(sortFactoryRows);
  }, [statusQuery.data, readinessQuery.data, onlyFactoryId, onlyFactoryNumber]);

  const visibleTree = useMemo(
    () => filterTouFactoryTree(buildTouFactoryTree(rows), search),
    [rows, search],
  );

  useEffect(() => {
    if (!rows.length) return;
    if (!rows.some((row) => factoryQueryId(row.factoryId) === factoryQueryId(selected))) {
      setSelected(factoryQueryId(rows[0].factoryId));
    }
  }, [rows, selected]);

  const blocked = useMemo(() => {
    if (!reportMode) return [];
    return rows.filter((row) => row.hadCharging && row.status !== 'COMPLETE');
  }, [reportMode, rows]);

  const current = rows.find((row) => factoryQueryId(row.factoryId) === factoryQueryId(selected));
  const loading = statusQuery.isLoading || readinessQuery.isLoading;
  const selectedComplete = current?.status === 'COMPLETE' && !dirty && !loading;
  const singleFactory =
    (onlyFactoryId != null && onlyFactoryId !== '') ||
    String(onlyFactoryNumber || '').trim() !== '';
  const onReadyRef = useRef(onReadyChange);
  onReadyRef.current = onReadyChange;

  useEffect(() => {
    onReadyRef.current?.({
      ready: singleFactory
        ? selectedComplete
        : reportMode
          ? blocked.length === 0 && !dirty && !loading
          : true,
      dirty,
      loading,
      blockedLabel: blocked.map((row) => factoryLabel(row, t('fallbackRow'))).join(', '),
    });
  }, [blocked, dirty, reportMode, loading, selectedComplete, singleFactory, t]);

  const copyOptions = (statusQuery.data?.factories || [])
    .filter((row) => String(row.factoryId) !== String(selected))
    .filter((row) => row.kind !== 'PARENT' && factoryQueryId(row.factoryId) !== 'fallback')
    .map((row) => ({ value: String(row.factoryId), label: factoryLabel(row, row.number) }));

  const copyToFactories = async () => {
    if (!copyTargets.length) {
      message.warning(t('selectCopyTargets'));
      return;
    }
    try {
      await copy.mutateAsync({
        operatorId,
        sourceYear: year,
        sourceMonth: month,
        sourceFactoryId: factoryQueryId(selected),
        targetFactoryIds: copyTargets,
      });
      setCopyOpen(false);
      setCopyTargets([]);
      message.success(t('copySuccess'));
    } catch (error) {
      message.error(error?.response?.data?.message || t('copyFailed'));
    }
  };

  if (!loading && !rows.length) {
    return (
      <div className="rounded-md border border-gray-200 bg-white p-8">
        <Empty description={t('noFactories')} />
      </div>
    );
  }

  const renderFactoryRow = (row, depth = 0) => {
    const id = factoryQueryId(row.factoryId);
    const isHeader = Boolean(row.isGroupHeader);
    const active = !isHeader && id === factoryQueryId(selected);
    const hasChildren = Boolean(row.children?.length);
    const pad = { paddingLeft: 12 + depth * 14 };

    const statusTags = !isHeader ? (
      <span className="flex shrink-0 flex-col items-end gap-1">
        <Tag color={statusColor[row.status] || 'default'}>
          {t(row.status === 'COMPLETE' ? 'complete' : row.status === 'HAS_GAPS' ? 'hasGaps' : 'notSet')}
        </Tag>
        {row.hadCharging ? <Tag color="blue">{t('hadCharging')}</Tag> : null}
      </span>
    ) : null;

    const label = (
      <span className="min-w-0">
        <span className={`block truncate ${isHeader || row.kind === 'PARENT' ? 'font-semibold text-gray-700' : 'font-medium'}`}>
          {row.name || row.number || t('fallbackRow')}
        </span>
        {!isHeader && row.number && (row.kind === 'CHILD' || (row.kind === 'PARENT' && row.name)) ? (
          <span className="block truncate text-xs text-gray-500">{row.number}</span>
        ) : null}
        {isHeader && hasChildren ? (
          <span className="block text-xs text-gray-400">
            {t('childCount', { count: row.children.length })}
          </span>
        ) : null}
      </span>
    );

    return (
      <div key={id}>
        {isHeader ? (
          <div
            className="flex w-full items-center justify-between gap-2 border-b border-gray-100 bg-slate-50 px-3 py-2 text-left"
            style={pad}
          >
            {label}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setSelected(id)}
            className={`flex w-full items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 text-left ${
              active ? 'bg-blue-50' : 'bg-white'
            }`}
            style={pad}
          >
            {label}
            {statusTags}
          </button>
        )}
        {hasChildren ? (
          <div className={depth === 0 ? 'border-l-2 border-slate-200 ml-3' : ''}>
            {row.children.map((child) => renderFactoryRow(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="grid gap-4 md:grid-cols-[300px_1fr]">
      <div>
        {!onlyFactoryId ? (
          <Input
            allowClear
            placeholder={t('searchFactories')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="mb-2"
          />
        ) : null}
        <div className="max-h-[520px] overflow-auto rounded-md border border-gray-200 bg-white">
          {visibleTree.map((row) => renderFactoryRow(row))}
        </div>
      </div>
      <div className="rounded-md border border-gray-200 bg-white p-4">
        <Title level={5} className="mt-0!">
          {factoryLabel(current, t('fallbackRow'))}
        </Title>
        <Text type="secondary">
          {t('appliesTo', {
            factory: current?.isFallback ? t('fallbackRow') : factoryLabel(current, ''),
            month: `${month}/${year}`,
          })}
        </Text>
        <div className="mt-3">
          {selected ? (
          <FactoryTouEditor
            key={`${selected}-${year}-${month}`}
            operatorId={operatorId}
            year={year}
            month={month}
            factoryId={selected}
            onDirtyChange={setDirty}
            extraActions={
              !onlyFactoryId ? (
                <Button onClick={() => setCopyOpen(true)}>{t('copyToFactories')}</Button>
              ) : null
            }
          />
          ) : null}
        </div>
      </div>
      <Modal
        title={t('copyToFactories')}
        open={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onOk={copyToFactories}
        okButtonProps={{ disabled: !copyTargets.length }}
        confirmLoading={copy.isPending}
      >
        {copyOptions.length ? (
          <Checkbox.Group
            className="flex flex-col gap-2"
            options={copyOptions}
            value={copyTargets}
            onChange={setCopyTargets}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noCopyTargets')} />
        )}
      </Modal>
      {reportMode && blocked.length > 0 ? (
        <Alert
          className="md:col-span-2"
          type="warning"
          showIcon
          message={t('reportBlocked', { factories: blocked.map((row) => factoryLabel(row, t('fallbackRow'))).join(', ') })}
        />
      ) : null}
    </div>
  );
}
